/**
 * /integraciones — hub listing every AI-client integration. ChatGPT links to
 * its dedicated page (/chatgpt); the rest go to /integraciones/:slug.
 * Server injects SEO for crawlers; this effect covers SPA navigation.
 */
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Bot, Home, Plug } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { SiteFooter } from '../components/SiteFooter';
import { INTEGRATIONS } from '../lib/integrations';

const CHATGPT = { slug: 'chatgpt', name: 'ChatGPT', to: '/chatgpt', shortDescription: 'Gestiona tus redes hablando con ChatGPT mediante el conector MCP.', icon: Bot };

export function Integrations() {
  useEffect(() => {
    const prev = document.title;
    document.title = 'Integraciones de Zettapost con IA (ChatGPT, Claude, Cursor…) · Zettapost';
    return () => { document.title = prev; };
  }, []);

  const cards = [CHATGPT, ...INTEGRATIONS.map((i) => ({ slug: i.slug, name: i.name, to: `/integraciones/${i.slug}`, shortDescription: i.shortDescription, icon: i.icon }))];

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Navbar />

      <section className="relative overflow-hidden bg-gradient-to-br from-white via-brand-50/40 to-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-12 lg:py-16">
          <nav aria-label="Breadcrumbs" className="text-xs text-slate-500">
            <ol className="inline-flex items-center gap-1.5">
              <li><Link to="/" className="inline-flex items-center gap-1 hover:text-slate-900 transition"><Home size={11} />Inicio</Link></li>
              <li className="text-slate-300">/</li>
              <li className="text-slate-700">Integraciones</li>
            </ol>
          </nav>
          <span className="mt-5 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-brand-700"><Plug size={13} /> Conector MCP</span>
          <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-slate-900 max-w-3xl">
            Conecta Zettapost con tu IA favorita
          </h1>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-2xl">
            Publica, programa y mide tus redes sociales hablando con la IA. Un mismo conector MCP, seguro y por OAuth, para todos tus clientes favoritos.
          </p>
        </div>
      </section>

      <section>
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-12 lg:py-16">
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {cards.map((c) => {
              const Icon = c.icon;
              return (
                <Link key={c.slug} to={c.to} className="group flex flex-col rounded-2xl border border-slate-200 bg-white p-5 hover:border-brand-300 hover:shadow-md transition">
                  <div className="h-10 w-10 rounded-lg bg-brand-50 text-brand-700 inline-flex items-center justify-center"><Icon size={18} /></div>
                  <h2 className="mt-3 font-semibold text-slate-900 group-hover:text-brand-700 transition">{c.name}</h2>
                  <p className="mt-1.5 text-sm text-slate-500 leading-relaxed flex-1">{c.shortDescription}</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-brand-600 group-hover:text-brand-700">Ver integración<ArrowRight size={13} className="transition group-hover:translate-x-0.5" /></span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 border-t border-slate-200">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-12 lg:py-16">
          <div className="rounded-3xl bg-gradient-to-br from-brand-600 to-brand-800 text-white p-8 lg:p-12 text-center">
            <Bot size={28} className="mx-auto opacity-90" />
            <h2 className="mt-3 text-2xl lg:text-3xl font-bold">Tu IA, gestionando tus redes</h2>
            <p className="mt-2 text-brand-100 max-w-xl mx-auto">Conecta tu cuenta con OAuth en menos de un minuto. Incluido en todos los planes. 3 días gratis.</p>
            <Link to="/register" className="mt-6 inline-flex items-center gap-1.5 rounded-lg bg-white text-brand-700 font-semibold px-5 py-2.5 hover:bg-brand-50 transition">Crear cuenta gratis<ArrowRight size={15} /></Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
